/**
 * @fileoverview Sandboxed iframe-based preview that renders a DocumentLayout to HTML.
 * @module document-builder
 *
 * Responsibilities: - Convert a DocumentLayout to an HTML string via renderDocumentLayoutToHtml.
 *                   - When resolved `renderData` is supplied, render the data-driven HTML
 *                     (fully expanded sections + department branding) via the shared renderer
 *                     so dynamic content (header, violations, recipient, footer) is visible.
 *                   - Display the result in an isolated iframe so builder CSS cannot bleed.
 *                   - Auto-size the iframe height to fit the rendered document.
 * Dependencies:     - @firesquirrel/document-render: renderDocumentLayoutToHtml,
 *                     renderLayoutDocumentToHtml, DocumentLayout, DocumentRenderData.
 * Architecture:     - Pure presentation; no data access, no API calls, no Dexie reads. The host
 *                     builder owns resolving `renderData` (e.g. from a selected department).
 * Notes:            - Always pass `documentLayout ?? buildCanvasLayout()` from the host builder.
 *                   - Height measurement reads the iframe's same-origin contentDocument after load.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { renderDocumentLayoutToHtml, renderLayoutDocumentToHtml } from '@firesquirrel/document-render';
import type { DocumentLayout, DocumentRenderData } from '@firesquirrel/document-render';

export function sanitizeHtmlForIframe(html: string): string {
  return DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    USE_PROFILES: { html: true, svg: true, svgFilters: true },
    // Document HTML is fully author-controlled (generated by our own renderers, not
    // user-typed). Allow inline `style` attributes so checkbox spans, theme colors, and
    // all other inline-styled document elements render correctly in the iframe.
    // Script/event-handler removal is still enforced by DOMPurify regardless of this flag.
    FORCE_BODY: false,
    ADD_ATTR: ['style'],
  });
}

/**
 * @contract DocumentLayoutHtmlPreviewProps
 * Purpose: Props for the DocumentLayoutHtmlPreview component.
 * Fields:  layout — the canonical DocumentLayout to render; scale — display scale factor (default 0.75).
 * Rules:   Callers MUST pass `documentLayout ?? buildCanvasLayout()` so Canvas and Sections edits are both reflected.
 * Sync:    N/A — pure presentation, no persistence.
 */
interface DocumentLayoutHtmlPreviewProps {
  /** The canonical DocumentLayout to render. Pass `documentLayout ?? buildCanvasLayout()`. */
  layout: DocumentLayout;
  /**
   * Optional resolved render data. When provided, the preview renders the data-driven HTML
   * (expanded sections + department branding) instead of layout-only binding markers, so the
   * preview reflects real content for the selected/implicit department. When omitted, the
   * existing layout-only rendering is used (backward compatible).
   */
  renderData?: DocumentRenderData;
  /** CSS scale factor applied via transform. Defaults to 0.75. */
  scale?: number;
}

/**
 * @component DocumentLayoutHtmlPreview
 * Purpose:     Renders a DocumentLayout as pixel-accurate HTML inside an isolated iframe.
 *              Uses the same renderDocumentLayoutToHtml function as the PDF worker, making the
 *              preview structurally identical to the final PDF output (decision A2 / C19).
 * Props:       layout — DocumentLayout to render; scale — display zoom (default 0.75).
 * Behavior:    On mount and on every layout change, regenerates the iframe HTML and auto-sizes
 *              the iframe height based on the rendered content's scrollHeight.
 * Data Source: local (pure render — no API, no Dexie)
 * Constraints: No direct API calls. CSS is fully isolated inside the iframe.
 * Performance: Re-renders when `layout` or `renderData` reference changes. The iframe document is a full HTML document.
 *
 * @reusable
 * Usage: Import from @firesquirrel/ui-admin. Used in SystemDocumentBuilderPage and DocumentTemplateBuilderPage
 *        for both the right-panel live preview and the full-screen Preview modal.
 * Rules: presentation-only — no state, no side effects other than iframe height adjustment on load.
 */
export function DocumentLayoutHtmlPreview({ layout, renderData, scale = 0.75 }: DocumentLayoutHtmlPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Data-driven path: renderLayoutDocumentToHtml emits a complete HTML document (page chrome +
  // expanded sections). Layout-only path: a fragment we wrap in our own minimal document.
  const html = useMemo(() => {
    const srcdoc = renderData
      ? renderLayoutDocumentToHtml(layout, renderData)
      : [
          '<!DOCTYPE html>',
          '<html>',
          '<head><meta charset="utf-8"><style>',
          'html,body{margin:0;padding:0;background:#f3f4f6;}',
          '</style></head>',
          `<body>${renderDocumentLayoutToHtml(layout)}</body>`,
          '</html>',
        ].join('');
    return sanitizeHtmlForIframe(srcdoc);
  }, [layout, renderData]);

  const writeFrame = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
  }, [html]);

  useEffect(() => {
    writeFrame();
    return () => {
      const iframe = iframeRef.current;
      if (!iframe?.contentDocument) return;
      iframe.contentDocument.open();
      iframe.contentDocument.write('<!doctype html><html><head></head><body></body></html>');
      iframe.contentDocument.close();
    };
  }, [writeFrame]);

  /**
   * @errorHandling
   * Strategy:    Silently skips height adjustment if contentDocument is unavailable (cross-origin guard).
   * User Impact: iframe may show scrollbar if height cannot be measured; functionally correct.
   * Logging:     REQUIRED — no logging needed for silent graceful degradation on a non-critical affordance.
   */
  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) return;
    const scrollHeight = iframe.contentDocument.body.scrollHeight;
    if (scrollHeight > 0) {
      iframe.style.height = `${scrollHeight}px`;
    }
  }, []);

  const scaledWidth = scale < 1 ? `${Math.round(100 / scale)}%` : '100%';

  return (
    <div
      className="origin-top-left overflow-hidden"
      style={{ transform: `scale(${scale})`, width: scaledWidth }}
    >
      <iframe
        ref={iframeRef}
        src="about:blank"
        onLoad={handleLoad}
        title="Document layout preview"
        style={{
          border: 'none',
          width: '100%',
          minHeight: 200,
          display: 'block',
        }}
      />
    </div>
  );
}

export default DocumentLayoutHtmlPreview;
